import React from "react";
import { Link } from "react-router-dom";
import MarketingNav from "@/components/MarketingNav";
import QuoteCTA from "@/components/QuoteCTA";
import QuoteForm from "@/components/QuoteForm";
import Seo from "@/components/Seo";
import { BIZ } from "@/data/site";
import { MapPin, Phone, ArrowRight, Calculator } from "lucide-react";

export default function ServiceAreasPage() {
  return (
    <div className="bg-paper min-h-screen">
      <Seo
        title={`Service Areas — Pressure Washing in ${BIZ.region}`}
        description={`We serve homes and businesses in ${BIZ.serviceAreas.join(", ")}, Tennessee. Fast, local, licensed & insured pressure washing.`}
        path="/service-areas/"
      />
      <MarketingNav />
      <QuoteCTA />

      <section className="pt-6 md:pt-8 pb-16 md:pb-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-2xl">
            <div className="text-xs font-bold uppercase tracking-[0.25em] text-brand-700 mb-4 flex items-center gap-2">
              <MapPin className="w-3.5 h-3.5" />
              Service areas
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight text-stone-900 leading-[1.05]" style={{ fontFamily: "Outfit" }}>
              Local to<br />
              <span className="text-electric-500 italic">Middle Tennessee.</span>
            </h1>
            <p className="mt-6 text-lg text-stone-600">
              Wynridge Exterior Cleaning is a service-area business based in <b>{BIZ.city}, {BIZ.stateShort} {BIZ.zip}</b>. We travel to homes across seven neighboring communities. Not sure if your address is in range? Call us and we&apos;ll tell you straight.
            </p>
            <a
              href={`tel:${BIZ.phoneTel}`}
              className="mt-6 inline-flex items-center gap-2 text-brand-700 font-bold hover:text-electric-500 transition-colors"
            >
              <Phone className="w-4 h-4" />
              {BIZ.phone}
            </a>
          </div>
        </div>
      </section>

      <section className="pb-16 md:pb-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="service-areas-grid">
          {BIZ.serviceAreas.map((city) => (
            <div
              key={city}
              data-testid={`area-${city.toLowerCase().replace(/\s+/g, "-")}`}
              className="p-6 rounded-2xl bg-white border border-stone-200 hover:border-electric-500 hover:shadow-lg transition-all"
            >
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-xl bg-brand-100 flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-brand-700" />
                </div>
                <div>
                  <div className="font-bold text-stone-900 text-lg" style={{ fontFamily: "Outfit" }}>{city}</div>
                  <div className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">Tennessee</div>
                </div>
              </div>
              <p className="mt-4 text-sm text-stone-600 leading-relaxed">
                Full residential exterior cleaning in {city} — house washing, roofs, driveways, gutters, windows, and light-commercial properties.
              </p>
              <Link
                to="/pressure-washing-estimate-calculator/"
                className="mt-4 inline-flex items-center gap-2 text-sm font-bold text-brand-700 hover:text-electric-500"
              >
                Get an instant estimate
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          ))}
        </div>
        <div className="max-w-7xl mx-auto px-6 md:px-12 mt-10 flex flex-wrap gap-3">
          <Link
            to="/pressure-washing-estimate-calculator/"
            className="inline-flex items-center gap-2 rounded-full bg-brand-600 hover:bg-electric-500 text-white font-bold px-6 py-3 shadow-lg transition-all hover:-translate-y-0.5"
          >
            <Calculator className="w-4 h-4" />
            Get an instant estimate
          </Link>
          <a
            href={BIZ.gbpUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-full border border-stone-200 bg-white text-stone-900 hover:border-stone-300 font-bold px-6 py-3 transition-all hover:-translate-y-0.5"
          >
            See our Google Business Profile
          </a>
        </div>
      </section>

      <QuoteForm />

      <footer className="bg-stone-900 text-stone-400 py-12 text-center text-xs">
        © {new Date().getFullYear()} {BIZ.name} · {BIZ.city}, {BIZ.stateShort} {BIZ.zip}
      </footer>
    </div>
  );
}
